import { APP_NAME, APP_TAGLINE, BRAND } from "@/constants/config";
import { ROUTE_PATTERNS, localeFromPathname, publicLocalePath, workspaceSectionPath } from "@/constants/routes";
import { getDictionary } from "@bizy/core/i18n/resolve";
import { cn } from "@/lib/utils";
import { usePreferences } from "@/providers/preferences-provider";
import { useAuth } from "@/providers/auth-provider";
import { useDemoWorkspace } from "@/hooks/use-demo-workspace";
import { LayoutDashboard } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import { LocaleSwitcher } from "./locale-switcher";
import { ThemeToggle } from "./theme-toggle";

export const BrandMark = ({ className }: { className?: string }) => (
  <span
    className={cn(
      "flex size-8 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground",
      className,
    )}
    aria-hidden
  >
    {BRAND.mark}
  </span>
);

/**
 * Public navbar (PRD §2.1) for the preview build: the "Dashboard" CTA goes
 * straight into the demo workspace instead of a selector or a sign-in page.
 */
export const PublicNavbar = ({ workspaceSlug }: { workspaceSlug?: string }) => {
  const { locale: preference } = usePreferences();
  const { actor } = useAuth();
  const demo = useDemoWorkspace();
  const location = useLocation();
  // The URL wins on public pages; a stored preference can lag behind a shared link.
  const locale = localeFromPathname(location.pathname) ?? preference;
  const dictionary = getDictionary(locale);

  const targetSlug = workspaceSlug ?? demo.slug;
  const dashboardTarget = targetSlug
    ? workspaceSectionPath(targetSlug, "home")
    : ROUTE_PATTERNS.dashboard;

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur">
      <nav className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-4 px-4">
        <Link to={publicLocalePath(locale)} className="flex items-center gap-2">
          <BrandMark />
          <span className="flex flex-col leading-tight">
            <span className="text-base font-semibold tracking-tight">{APP_NAME}</span>
            <span className="hidden text-[10px] uppercase tracking-wide text-muted-foreground sm:block">
              {APP_TAGLINE}
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          <a href="#features" className="text-sm text-muted-foreground hover:text-foreground">
            {dictionary.nav.features}
          </a>
          <a href="#workflow" className="text-sm text-muted-foreground hover:text-foreground">
            {dictionary.nav.workflow}
          </a>
          <a href="#plans" className="text-sm text-muted-foreground hover:text-foreground">
            {dictionary.nav.pricing}
          </a>
        </div>

        <div className="flex items-center gap-2">
          <LocaleSwitcher className="hidden sm:inline-flex" />
          <ThemeToggle className="hidden sm:inline-flex" />

          <Button asChild size="sm">
            <Link to={dashboardTarget}>
              <LayoutDashboard className="size-4" aria-hidden />
              {dictionary.nav.dashboard}
            </Link>
          </Button>

          {actor ? (
            <span
              className="hidden items-center gap-2 rounded-full border border-border p-0.5 pr-3 text-xs sm:flex"
              title={`Preview mode · ${actor.email}`}
            >
              {actor.avatar_url ? (
                <img src={actor.avatar_url} alt="" className="size-7 rounded-full object-cover" />
              ) : (
                <span className="flex size-7 items-center justify-center rounded-full bg-muted text-xs">
                  {actor.email.slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="max-w-24 truncate">{actor.display_name ?? "Preview"}</span>
            </span>
          ) : null}
        </div>
      </nav>
    </header>
  );
};
